import type { RowData } from "@tanstack/react-table";
import { type JSX, memo, useMemo, useRef } from "react";

import { TableContainer } from "@components/organisms/pageTable/components/tableContainer";
import { TableHead } from "@components/organisms/pageTable/components/tableHead";
import type {
  IPageTableProps,
  ITableHeadingProps,
} from "@components/organisms/pageTable/types";
import { TableBody } from "@components/organisms/pageTable/components/tableBody";
import { TableBottom } from "@components/organisms/pageTable/components/tableBottom";

export const PageTable = memo(function PageTable<TData extends RowData>(
  props: IPageTableProps<TData>,
) {
  const headingProps: ITableHeadingProps = useMemo(
    () => ({
      onDownload: props.onDownload,
      onPrint: props.onPrint,
      icon: props.icon,
      tabProps: props.tabProps,
      title: props.title,
    }),
    [props.icon, props.onDownload, props.onPrint, props.tabProps, props.title],
  );

  const tableFilterSearchProps = useMemo(
    () => ({
      searchProps: props.searchProps,
      filterProps: props.filterProps,
      actions: props.actions,
      placeholder: props.placeholder,
    }),
    [props.actions, props.filterProps, props.placeholder, props.searchProps],
  );

  const ref = useRef<HTMLDivElement>(null);

  return (
    <TableContainer ref={ref}>
      <TableHead
        headingProps={headingProps}
        tableFilterSearchProps={tableFilterSearchProps}
      />
      {props?.type === "basic" && (
        <TableBody
          type={props.type}
          isLoading={props.isLoading}
          isError={props.isError}
          total={props.total}
          tableProps={props.tableProps}
          isFetching={props.isFetching}
          loaderType={props.loaderType}
        />
      )}
      {props?.type !== "basic" && (
        <TableBody
          type={props.type}
          isLoading={props.isLoading}
          isError={props.isError}
          total={props.total}
          tableProps={props.tableProps}
          isFetching={props.isFetching}
          loaderType={props.loaderType}
        >
          {props?.children}
        </TableBody>
      )}
      {(props?.total || 0) > 0 && (
        <TableBottom
          total={props.total}
          isLoading={props.isLoading}
          isError={props.isError}
          loaderType={props.loaderType}
          isFetching={props.isFetching}
          pagination={props.pagination}
          tableRef={ref}
        />
      )}
    </TableContainer>
  );
}) as <TData extends RowData>(props: IPageTableProps<TData>) => JSX.Element;
